import React, { type PropsWithChildren, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { some } from "lodash";
import { LeftArrowButton, RightArrowButton } from "../buttons/arrowButtons";
import { ProgressDrawerButton } from "../buttons/progressDrawerButton";
import TopMenuAccordion from "./TopMenuAccordion/TopMenuAccordion";
import ProgressDrawer from "../progressDrawer/ProgressDrawer";
import { RouteNames } from "../../enums";
import arrayUtils from "../../utils/arrayUtils";
import Logo from "./logo/Logo";
import FeedbackButton from "../feedbackButton/FeedbackButton";
import { useLocation } from "react-router";
import AccountSettingsMenu from "./accountSettings/AccountSettingsMenu";

import "./navigation.scss";
import { type TaskPool } from "../../features/BackgroundTasks/taskPool";

const getActiveRouteItems = (pathname: string) => {
  const possibleRoutes = RouteNames.allRoutes;
  const twoLevels = pathname.split("/").slice(1, 3);
  const activeRouteItems = [];
  for (let i = 1; i <= twoLevels.length; i++) {
    const route = twoLevels.slice(0, i).join("/");
    if (possibleRoutes.includes(route)) {
      activeRouteItems.push(route);
    }
  }

  return activeRouteItems;
};

const collapsedStateInitiallyExpandedItems = [
  RouteNames.linkedAccountsManagement,
  RouteNames.accountSettingsManagement,
  RouteNames.content,
];

const collapsedStateAvailableItems = [...collapsedStateInitiallyExpandedItems, RouteNames.contentSimulatedPhishing];

export type NavigationProps = PropsWithChildren & {
  accountId: number;
  backgroundTasks: TaskPool;
  backgroundTasksElapsedTime?: string;
  collapsed: boolean;
  hideProgressDrawer: () => void;
  isCollapsedByUser: boolean;
  isProgressDrawerVisible: boolean;
  isWizardOpened: boolean;
  moboAccountLogoUrl?: string;
  moboAccountName?: string;
  onDismissAllClick: () => void;
  onSignout: () => void;
  showProgressDrawer: () => void;
  autoCollapseSidebar: () => void;
  onCollapse: () => void;
  onExpand: () => void;
};

export const Navigation = (props: NavigationProps) => {
  const {
    accountId,
    backgroundTasks,
    backgroundTasksElapsedTime,
    children,
    collapsed,
    hideProgressDrawer,
    isCollapsedByUser,
    isProgressDrawerVisible,
    isWizardOpened,
    moboAccountLogoUrl,
    moboAccountName,
    onDismissAllClick,
    onSignout,
    showProgressDrawer,
    autoCollapseSidebar,
    onExpand,
    onCollapse,
  } = props;

  const [activeItems, setActiveItems] = useState<string[]>([]);
  // Redraws accountName to determine if popup is necessary
  const [disableTooltip, setDisableTooltip] = useState(true);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const iconRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const activeRouteItems = getActiveRouteItems(location.pathname);
    if (collapsed) {
      const routeItems = activeRouteItems.filter((a) => collapsedStateAvailableItems.includes(a));
      setActiveItems([...collapsedStateInitiallyExpandedItems, ...routeItems]);
    } else {
      setActiveItems(activeRouteItems);
    }
  }, [collapsed, location.pathname]);

  useEffect(() => {
    if (!isCollapsedByUser && isWizardOpened) {
      autoCollapseSidebar();
      setActiveItems(collapsedStateInitiallyExpandedItems);
    } else if (!isCollapsedByUser && !isWizardOpened) {
      onExpand();
    }
  }, [isWizardOpened]); // eslint-disable-line

  const handleTopLevelItemClick = (path: string = "") => {
    if (collapsed && !collapsedStateAvailableItems.includes(path)) {
      return;
    }

    if (activeItems.includes(path)) {
      setActiveItems(arrayUtils.withoutItem(activeItems, path));
    } else {
      setActiveItems([...activeItems, path]);
    }
  };

  const renderExpandCollapseControls = () => {
    let activeButton;
    if (collapsed) {
      activeButton = <RightArrowButton onClick={onExpand} />;
    } else {
      activeButton = <LeftArrowButton onClick={onCollapse} />;
    }
    return <div className="panel-width-controls">{activeButton}</div>;
  };

  useEffect(() => {
    if (collapsed) {
      return setDisableTooltip(true);
    }

    const delayWhileOpening = 600;
    setTimeout(() => {
      setDisableTooltip(false);

      if (dropdownRef.current && iconRef.current) {
        dropdownRef.current.style.setProperty(
          "--dynamic-position",
          `${iconRef.current.getBoundingClientRect().x - 9}px`,
        );
      }
    }, delayWhileOpening);
  }, [collapsed]);

  return (
    <div className={cn("navigation", { collapsed })}>
      <nav className="navbar pusher scrollable-content">
        <Logo moboAccountName={moboAccountName} moboAccountLogoUrl={moboAccountLogoUrl} collapsed={collapsed} />
        {renderExpandCollapseControls()}

        <TopMenuAccordion activeItems={activeItems} collapsed={collapsed} topLevelItemClick={handleTopLevelItemClick} />

        <ProgressDrawer
          visible={isProgressDrawerVisible}
          hideDrawer={hideProgressDrawer}
          tasks={backgroundTasks}
          elapsedTime={backgroundTasksElapsedTime}
          onDismissAllClick={onDismissAllClick}
        />
        <div
          className={cn("bottom-buttons", {
            "bottom-buttons-collapsed": collapsed,
          })}
        >
          {backgroundTasks && some(backgroundTasks, (x) => !x.isHidden) && (
            <ProgressDrawerButton
              tasks={backgroundTasks}
              onClick={showProgressDrawer}
              isCollapsed={collapsed || isCollapsedByUser}
            />
          )}
          <AccountSettingsMenu
            collapsed={collapsed}
            accountId={accountId}
            disableTooltip={disableTooltip}
            iconRef={iconRef}
            dropdownRef={dropdownRef}
            onSignout={onSignout}
          />
        </div>
      </nav>
      <main className="pushable">{children}</main>

      <FeedbackButton />
    </div>
  );
};

export default Navigation;
